import { useMemo, useState } from 'react';
import { api, ApiError, formatDateTime, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useRefData } from '../../lib/refdata';
import type { Vendor, Voucher } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

const TABS = [
  { key: 'TREASURY_QUEUE', label: 'Incoming queue' },
  { key: 'TREASURY_VERIFIED', label: 'Verified' },
  { key: 'SCHEDULED', label: 'Scheduled' },
  { key: 'PAID', label: 'Paid' },
  { key: 'RETURNED', label: 'Returned' },
] as const;

export function TreasuryQueue() {
  const { persona } = useAuth();
  const { votes } = useRefData();
  const [tab, setTab] = useState<string>('TREASURY_QUEUE');
  const [actionError, setActionError] = useState<string | null>(null);
  const [returnTarget, setReturnTarget] = useState<Voucher | null>(null);

  const listQ = useApiQuery<{ items: Voucher[] }>(`/api/v1/expenditure/vouchers?status=${tab}&limit=100`);
  const vendorsQ = useApiQuery<{ items: Vendor[] }>('/api/v1/commitments/vendors?limit=100');
  const vendorName = useMemo(() => {
    const m = new Map((vendorsQ.data?.items ?? []).map((v) => [v.id, v.name]));
    return (id: string) => m.get(id) ?? id;
  }, [vendorsQ.data]);
  const voteName = (id: string) => {
    const v = votes.find((x) => x.id === id);
    return v ? `${v.code} ${v.name}` : id;
  };

  const canVerify = persona?.roles.includes('TREASURY_VERIFIER') ?? false;
  const canSchedule = persona?.roles.includes('TREASURY_SUPERVISOR') ?? false;

  const act = async (v: Voucher, action: 'verify' | 'schedule') => {
    setActionError(null);
    try {
      await api.post(`/api/v1/expenditure/vouchers/${v.id}/${action}`);
      listQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : `${action} failed`);
    }
  };

  return (
    <div>
      <PageHeader
        title="Voucher verification & scheduling"
        subtitle="Ministry vouchers arrive here — verification liquidates the commitment, scheduling hands off to payment batching"
      />
      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}

      <div className="mb-4 flex gap-1 rounded-xl bg-slate-200/60 p-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex-1 rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
              tab === t.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="Nothing here" hint="Vouchers appear as ministries submit them to Treasury." />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Voucher</th>
                <th className="table-head">Ministry</th>
                <th className="table-head">Suppliers</th>
                <th className="table-head text-right">Amount</th>
                <th className="table-head">MDA approvals</th>
                <th className="table-head">Status</th>
                <th className="table-head" />
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((v) => (
                <tr key={v.id} className="hover:bg-slate-50">
                  <td className="table-cell font-medium">{v.number}</td>
                  <td className="table-cell text-xs">{voteName(v.voteId)}</td>
                  <td className="table-cell text-xs text-slate-500">
                    {[...new Set((v.invoices ?? []).map((i) => vendorName(i.vendorId)))].join(', ') || '—'}
                  </td>
                  <td className="table-cell text-right font-semibold tabular-nums">{scr(v.amount)}</td>
                  <td className="table-cell text-[11px] text-slate-400">
                    {(v.mdaApprovals ?? []).map((a) => a.role.replaceAll('_', ' ')).join(' · ') || '—'}
                    {v.scheduledAt ? <div>scheduled {formatDateTime(v.scheduledAt)}</div> : null}
                  </td>
                  <td className="table-cell">
                    <StatusBadge status={v.status} />
                  </td>
                  <td className="table-cell text-right">
                    <div className="flex justify-end gap-1.5">
                      {canVerify && v.status === 'TREASURY_QUEUE' ? (
                        <>
                          <button className="btn-success !px-2.5 !py-1 text-xs" onClick={() => act(v, 'verify')}>
                            Verify
                          </button>
                          <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => setReturnTarget(v)}>
                            Return
                          </button>
                        </>
                      ) : null}
                      {canSchedule && v.status === 'TREASURY_VERIFIED' ? (
                        <button className="btn-primary !px-2.5 !py-1 text-xs" onClick={() => act(v, 'schedule')}>
                          Schedule for payment
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {returnTarget ? (
        <ReturnModal
          voucher={returnTarget}
          onClose={() => setReturnTarget(null)}
          onDone={() => {
            setReturnTarget(null);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function ReturnModal({ voucher, onClose, onDone }: { voucher: Voucher; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/expenditure/vouchers/${voucher.id}/return`, { reason });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Return failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Return ${voucher.number} to ministry`} onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Reason for return">
          <textarea
            className="field-input"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Invoice document illegible — re-scan and resubmit"
          />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-danger" onClick={submit} disabled={busy || reason.length < 3}>
            {busy ? 'Returning…' : 'Return voucher'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
